import { PlusIcon } from "@phosphor-icons/react";
import { createFileRoute } from "@tanstack/react-router";
import { type ReactNode, useEffect, useState } from "react";
import { AddLibraryAssetModal } from "../components/AddLibraryAssetModal";
import { AddProjectAssetModal } from "../components/AddProjectAssetModal";
import { GalleryAssetGrid } from "../components/gallery/GalleryAssetGrid";
import { GalleryDesignGrid } from "../components/gallery/GalleryDesignGrid";
import { GalleryFolderList } from "../components/gallery/GalleryFolderList";
import { GalleryProjectDrawer } from "../components/gallery/GalleryProjectDrawer";
import { GallerySessionDetailPanel } from "../components/gallery/GallerySessionDetailPanel";
import { Header } from "../components/Header";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { type GalleryPageState, useGalleryPage } from "../hooks/useGalleryPage";
import {
  assetSectionTitle,
  filterAssetsByScope,
  ORG_ASSETS_EMPTY_MESSAGE,
} from "../lib/asset-scope";
import { gallerySearchSchema } from "../lib/route-schemas";

// Tree freshness comes from `refetchOnMount: "always"` on the query below.
export const Route = createFileRoute("/gallery")({
  validateSearch: gallerySearchSchema,
  component: GalleryPage,
});

function ProjectAssetsSidebar({
  page,
  projectId,
}: {
  page: GalleryPageState;
  projectId: string;
}) {
  const [isAddAssetOpen, setIsAddAssetOpen] = useState(false);

  // The project-assets query lives in useGalleryPage, which stays mounted
  // across the session-detail/refine views, so its refetchOnMount never fires.
  // This sidebar, however, only mounts when the project grid is shown — so
  // refetch here to pick up assets newly *derived* from designs (e.g. after
  // refining one) without needing a hard refresh.
  const { refetchProjectAssets } = page;
  useEffect(() => {
    refetchProjectAssets();
  }, [refetchProjectAssets]);

  return (
    <aside className="flex w-1/4 min-w-[280px] shrink-0 flex-col overflow-hidden">
      {/* Match the "Designs" header (GalleryDesignGrid): same font and no top
          padding so the two section titles sit on the same line. */}
      <div className="mb-3 flex items-center justify-between px-5">
        <p className="text-lg font-semibold leading-6 text-[var(--text-primary)]">
          Assets
        </p>
      </div>
      <div className="px-5 pb-5">
        {/* The hover lift/shadow lives on the dashed card so the card and the
            button inside it move together (mirrors the "Add New Design" card). */}
        <div className="flex items-center justify-center rounded-lg border border-dashed border-[var(--stroke-field)] px-4 py-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-xl">
          <button
            type="button"
            onClick={() => setIsAddAssetOpen(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--accent-secondary-stroke)] bg-[var(--Buttons-Secondary-Button---Fill)] px-4 py-2 text-sm font-semibold text-[var(--text-primary)] transition"
          >
            Add New Asset
            <PlusIcon size={14} weight="bold" aria-hidden />
          </button>
        </div>
      </div>
      <div className="scrollbar-hide flex-1 overflow-auto px-5 pb-6">
        {page.isProjectAssetsLoading ? (
          <div className="flex min-h-32 items-center justify-center">
            <LoadingSpinner size="md" />
          </div>
        ) : page.isProjectAssetsError ? (
          <div className="flex min-h-32 flex-col items-center justify-center gap-3 text-center">
            <p className="text-sm text-[var(--accent-destructive)]">
              Couldn&apos;t load this project&apos;s assets.
            </p>
            <button
              type="button"
              onClick={() => page.refetchProjectAssets()}
              className="text-sm font-semibold text-[var(--accent-primary)] hover:underline"
            >
              Try again
            </button>
          </div>
        ) : (
          <GalleryAssetGrid
            assets={page.projectAssets}
            singleColumn
            draggable={false}
            emptyMessage="No assets in this project yet. Add one to see it here."
          />
        )}
      </div>
      <AddProjectAssetModal
        isOpen={isAddAssetOpen}
        projectId={projectId}
        onClose={() => setIsAddAssetOpen(false)}
      />
    </aside>
  );
}

function GalleryDefaultContent({ page }: { page: GalleryPageState }) {
  const selectedProjectId =
    page.selectedNode.type === "project" ? page.selectedNode.projectId : null;
  return (
    <GalleryContentPanel>
      <div className="flex items-center justify-between px-7 py-5">
        <p className="text-[16px] leading-6 text-[var(--text-secondary)]">
          {page.selectedProject?.name ?? "My Projects"}
        </p>
      </div>
      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* pt-1 leaves room for the folder card's hover lift, which would
            otherwise clip against this scroll container's top edge. */}
        <div className="scrollbar-hide flex-1 overflow-auto px-7 pb-8 pt-1">
          {/* The default content panel only renders for root/project nodes, so
              the folder section is always shown here — even when a project has
              zero child folders, GalleryFolderList still offers the
              "Add New Folder +" card as its first item. */}
          <GalleryFolderList
            projects={page.enrichedProjects}
            onProjectClick={(projectId) =>
              page.navigate({ to: "/gallery", search: { projectId } })
            }
            onCreateFolder={page.handleCreateProject}
            onMoveSessionToProject={page.handleMoveSessionToProject}
          />
          <GalleryDesignGrid
            sessions={page.visibleSessions}
            hasMore={page.hasNextPage ?? false}
            isFetchingMore={page.isFetchingNextPage}
            onLoadMore={page.fetchNextPage}
            onSessionClick={page.handleSessionClick}
            onStartNewDesign={page.handleStartNewDesign}
          />
        </div>
        {selectedProjectId && (
          <ProjectAssetsSidebar page={page} projectId={selectedProjectId} />
        )}
      </div>
    </GalleryContentPanel>
  );
}

function GalleryAssetsContent({
  page,
  scope,
}: {
  page: GalleryPageState;
  scope: "personal" | "organization";
}) {
  const [isAddAssetOpen, setIsAddAssetOpen] = useState(false);
  const visibleAssets = filterAssetsByScope(page.libraryAssets, scope);
  const title = assetSectionTitle(scope, page.activeOrgName);
  const emptyMessage =
    scope === "organization" ? ORG_ASSETS_EMPTY_MESSAGE : undefined;

  return (
    <GalleryContentPanel>
      <div className="flex items-center justify-between px-7 py-5">
        <p className="text-[16px] leading-6 text-[var(--text-secondary)]">
          {title}
        </p>
        <button
          type="button"
          onClick={() => setIsAddAssetOpen(true)}
          className="inline-flex items-center gap-2 rounded-lg border border-[var(--accent-secondary-stroke)] bg-[var(--Buttons-Secondary-Button---Fill)] px-4 py-2 text-sm font-semibold text-[var(--text-primary)] transition-all hover:-translate-y-0.5 hover:shadow-xl"
        >
          Add New Asset
          <PlusIcon size={14} weight="bold" aria-hidden />
        </button>
      </div>
      <div className="scrollbar-hide flex-1 overflow-auto px-7 pb-8">
        {page.isAssetsLoading ? (
          <div className="flex min-h-64 items-center justify-center">
            <LoadingSpinner size="md" />
          </div>
        ) : page.isAssetsError ? (
          <div className="flex min-h-64 flex-col items-center justify-center gap-3 text-center">
            <p className="text-sm text-[var(--accent-destructive)]">
              Couldn&apos;t load your assets.
            </p>
            <button
              type="button"
              onClick={() => page.refetchAssets()}
              className="text-sm font-semibold text-[var(--accent-primary)] hover:underline"
            >
              Try again
            </button>
          </div>
        ) : (
          <GalleryAssetGrid
            assets={visibleAssets}
            emptyMessage={emptyMessage}
          />
        )}
      </div>
      <AddLibraryAssetModal
        isOpen={isAddAssetOpen}
        assetScope={scope}
        onClose={() => setIsAddAssetOpen(false)}
      />
    </GalleryContentPanel>
  );
}

function GalleryMainContent({
  page,
  selectedSessionId,
}: {
  page: GalleryPageState;
  selectedSessionId: string | undefined;
}) {
  if (page.selectedNode.type === "assets") {
    return <GalleryAssetsContent page={page} scope={page.selectedNode.scope} />;
  }
  if (selectedSessionId) {
    return (
      <GallerySessionDetailPanel
        sessionId={selectedSessionId}
        allProjects={page.projects}
        allSessions={page.slimSessions}
        onExitSessionView={page.exitSessionView}
        onGalleryNavigateRoot={page.selectRoot}
        onGalleryNavigateToProject={(projectId) =>
          page.navigate({ to: "/gallery", search: { projectId } })
        }
      />
    );
  }
  if (page.isLoading || page.isVisibleSessionsLoading)
    return <GalleryLoadingState />;
  if (
    page.error instanceof Error ||
    page.visibleSessionsError instanceof Error
  ) {
    return (
      <GalleryErrorState
        error={
          page.error instanceof Error
            ? page.error
            : (page.visibleSessionsError as Error)
        }
      />
    );
  }
  if (!page.hasGalleryContent) {
    return (
      <GalleryEmptyState
        onStartNewDesign={page.handleStartNewDesign}
        onCreateFolder={page.handleCreateProject}
      />
    );
  }
  return <GalleryDefaultContent page={page} />;
}

function GalleryPage() {
  const search = Route.useSearch();
  const gallery = useGalleryPage(
    search.projectId,
    search.sessionId,
    search.view,
    search.scope,
  );

  return (
    <div className="min-h-screen bg-[var(--bg-default)]">
      <Header currentPage="gallery" />
      <main className="mx-auto w-full max-w-[1800px] px-8 pb-8">
        <section className="h-[calc(100dvh-140px)] overflow-hidden rounded-2xl bg-[var(--bg-modal)] shadow-2xl">
          <div className="flex h-full">
            <GalleryProjectDrawer gallery={gallery} />
            <GalleryMainContent
              page={gallery}
              selectedSessionId={search.sessionId}
            />
          </div>
        </section>
      </main>
    </div>
  );
}

function GalleryContentPanel({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-1 flex-col bg-[#20272e]">{children}</div>
  );
}

function GalleryLoadingState() {
  return (
    <GalleryContentPanel>
      <div className="flex h-full min-h-96 items-center justify-center">
        <LoadingSpinner text="Loading gallery..." size="lg" />
      </div>
    </GalleryContentPanel>
  );
}

function GalleryErrorState({ error }: { error: Error }) {
  return (
    <GalleryContentPanel>
      <div className="flex h-full min-h-96 flex-col items-center justify-center text-center">
        <div className="text-xl text-[var(--accent-destructive)]">
          Error loading gallery
        </div>
        <p className="mt-2 text-[var(--text-secondary)]">
          {error.message || "An error occurred"}
        </p>
      </div>
    </GalleryContentPanel>
  );
}

function GalleryEmptyState({
  onStartNewDesign,
  onCreateFolder,
}: {
  onStartNewDesign: () => void;
  onCreateFolder: () => void | Promise<void>;
}) {
  return (
    <GalleryContentPanel>
      <div className="flex min-h-[460px] items-center justify-center">
        <div className="w-full max-w-xl rounded-xl border border-dashed border-[var(--stroke-field)]/50 px-8 py-16 text-center">
          <p className="text-sm text-[var(--text-primary)]">
            There are no designs in your gallery. Let&apos;s change that.
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={onStartNewDesign}
              className="inline-flex items-center gap-2 rounded-lg border border-[var(--accent-secondary-stroke)] px-4 py-2 text-sm font-semibold text-[var(--text-primary)] transition hover:bg-[var(--accent-primary)]/25"
            >
              Start New Design
              <PlusIcon size={14} weight="bold" aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => {
                onCreateFolder();
              }}
              className="inline-flex items-center gap-2 rounded-lg border border-[var(--accent-secondary-stroke)] px-4 py-2 text-sm font-semibold text-[var(--text-primary)] transition hover:bg-[var(--accent-primary)]/25"
            >
              New Folder
              <PlusIcon size={14} weight="bold" aria-hidden />
            </button>
          </div>
        </div>
      </div>
    </GalleryContentPanel>
  );
}
